import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSwitch } from '@nuvolo/nuux-core/hooks/useSwitch';
import styled from 'styled-components';
import imageNA from '#/static/image-na.webp';
import type { EditorProps } from './EditorProps';
import { backgroundColor, border, borderRadius } from './common';
import useAttachmentManager from '#/common/components/AttachmentUpload/hooks/useAttachmentManager';
import useToast from '#/common/hooks/useToast';
import TMMButton from '../../TMMButton';
import If from '../../If';
import StandardThrobber from '#/app/components/Throbbers/StandardThrobber';

const Container = styled.div<{ $height: string }>`
  position: relative;
  border: ${border};
  border-radius: ${borderRadius};
  background-color: ${backgroundColor};
  padding: 4px;
  height: ${(p) => p.$height};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover .overlay {
    opacity: 1;
    pointer-events: auto;
  }
`;

const Img = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
`;

const Overlay = styled.div<{ $disabled?: boolean }>`
  position: absolute;
  inset: 4px;
  border-radius: ${borderRadius};
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

export interface ImageOptions {
  height?: string;
  alt?: string;
  // context for manager
  relatedTable?: string;
  relatedItemId?: string;
  relatedField?: string;
  previewOnly?: boolean;
  disableActions?: boolean;
}

export interface ImageProps {
  editorType: 'Image';
  editorOptions: ImageOptions;
}

const Image: React.FC<EditorProps<ImageOptions>> = ({ value, editorOptions, form }) => {
  const {
    height = 'auto',
    alt,
    relatedTable = '',
    relatedItemId = form?.option('formData')?.id,
    relatedField,
    previewOnly,
    disableActions,
  } = editorOptions;

  const { alert, confirm } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { value: isNA, on: setNA, off: unsetNA } = useSwitch(!value);
  const [isLoading, setIsLoading] = React.useState(true);

  // Get tenantId from form data
  const tenantId = form?.option('formData')?.tenantId;

  const {
    selectedFiles,
    previewSrc,
    busyDeleteId,
    currentAttachments,
    onPickFile,
    clearPreview,
    upload,
    reupload,
    removeCurrentImage,
  } = useAttachmentManager({
    relatedTable,
    relatedItem: relatedItemId ?? '',
    attachmentType: 'profile',
    relatedField,
    tenantId,
  });

  const file = selectedFiles.length > 0 ? selectedFiles[0] : null;
  const preview = previewSrc[file?.name || ''];

  const currentImageFromAttachments = useMemo(() => {
    const imgs = (currentAttachments || []).filter(
      (a: any) => a?.type === 'profile' && String(a?.mimeType || '').includes('image'),
    );
    return imgs.length ? imgs[0] : null;
  }, [currentAttachments]);

  const displaySrc = useMemo(() => {
    if (preview) return preview;

    if (currentImageFromAttachments?.path && busyDeleteId !== currentImageFromAttachments.id) {
      return currentImageFromAttachments.downloadUrl || currentImageFromAttachments.path;
    }

    return imageNA;
  }, [preview, currentImageFromAttachments, busyDeleteId]);

  const onImgError = useCallback(() => setNA(), [setNA]);

  const triggerChoose = useCallback(() => {
    if (disableActions) return;
    fileInputRef.current?.click();
  }, [disableActions]);

  const onFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (!f) return;
      try {
        onPickFile(f);
        unsetNA();
      } catch (err: any) {
        alert('error', err?.message || 'Invalid file');
      } finally {
        // allow re-picking the same file
        e.target.value = '';
      }
    },
    [onPickFile, unsetNA, alert],
  );

  const onSaveNewImage = useCallback(async () => {
    try {
      if (currentImageFromAttachments) {
        await reupload();
      } else {
        await upload();
      }
      clearPreview();
      alert('success', `File "${file?.name}" uploaded successfully`);
      // Clear only the image field's dirty state — other form changes must be preserved.
      (form as any)?.clearFieldDirty?.(relatedField ?? 'picture');
    } catch (err: any) {
      alert('error', err?.message || 'Failed to upload image');
    }
  }, [upload, reupload, clearPreview, file, alert, form]);

  const onCancelPreview = useCallback(() => {
    setNA();
    clearPreview();
  }, [clearPreview]);

  const onDelete = useCallback(async () => {
    if (disableActions) return;
    if (!currentImageFromAttachments) return;

    const ok = await confirm(
      'warning',
      'Delete current picture? This will restore the placeholder.',
    );
    if (!ok) return;

    try {
      await removeCurrentImage();
      setNA();
      alert(
        'success',
        `File "${currentImageFromAttachments.originalFileName}" deleted successfully`,
      );
      clearPreview();
    } catch {
      alert('error', `Error deleting file: "${currentImageFromAttachments.originalFileName}"`);
    }
  }, [disableActions, currentImageFromAttachments, confirm, removeCurrentImage, alert]);

  useEffect(() => {
    const displayIsNA = displaySrc === imageNA;
    setIsLoading(isNA ? false : displayIsNA);
  }, [displaySrc]);

  return (
    <Container $height={height}>
      <If condition={!previewOnly && !!relatedItemId?.length}>
        {/* Hidden input for selecting the image */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={onFileInputChange}
        />

        {/* Hover overlay */}
        <Overlay className="overlay">
          {preview ? (
            <ButtonRow>
              <TMMButton
                disabled={disableActions}
                key="ok"
                type="submit"
                label="Save"
                onClick={onSaveNewImage}
              />
              <TMMButton
                disabled={disableActions}
                key="cancel"
                type="normal"
                label="Cancel"
                onClick={onCancelPreview}
              />
            </ButtonRow>
          ) : (
            <ButtonRow>
              {currentImageFromAttachments ? (
                <>
                  <TMMButton
                    disabled={disableActions}
                    key="ok"
                    type="normal"
                    label={'Re-Upload Picture'}
                    onClick={triggerChoose}
                  />
                  <TMMButton
                    disabled={disableActions}
                    key="cancel"
                    type="danger"
                    label="Delete"
                    onClick={onDelete}
                  />
                </>
              ) : (
                <TMMButton
                  disabled={disableActions}
                  key="ok"
                  type="normal"
                  label="Upload Picture"
                  onClick={triggerChoose}
                />
              )}
            </ButtonRow>
          )}
        </Overlay>
      </If>

      <If condition={isLoading}>
        <StandardThrobber />
      </If>
      <If condition={!isLoading}>
        <Img
          src={displaySrc}
          alt={typeof alt === 'string' ? alt : undefined}
          onError={onImgError}
        />
      </If>
    </Container>
  );
};

export default React.memo(Image);
